import { FC } from 'react';
import * as React from 'react';
import Multiclamp from 'react-multi-clamp';
import { useHistory } from 'react-router-dom';

import { clsx } from 'clsx';

import Diploma from '@crehana/icons/Diploma';
import PencilRuler from '@crehana/icons/PencilRuler';
import PlaySquareOutline from '@crehana/icons/PlaySquareOutline';
import { LazyImage } from '@crehana/ui';
import { useUITheme } from '@crehana/web';

import { useCrehanaTranslation } from '@/views/UserDashboard/hooks';
import {
  ENROLLMENTS_BUNDLES_ROUTE,
  ENROLLMENTS_CAREERS_ROUTE,
  ENROLLMENTS_ROUTE,
  ENROLLMENTS_TRACKS_ROUTE,
} from '@/views/UserDashboard/routes/config';

import { GetUserDashboardSearchData_userSearchData as SearchDataItem } from '../graphql/types/GetUserDashboardSearchData';

const Icons = {
  Course: PlaySquareOutline,
  Track: PlaySquareOutline,
  Career: PlaySquareOutline,
  Bundle: PlaySquareOutline,
  Project: PencilRuler,
  Certificate: Diploma,
};

interface SearchResultProps extends SearchDataItem {
  isLast?: boolean;
  onDismiss: () => void;
}

const SearchResult: FC<React.PropsWithChildren<SearchResultProps>> = ({
  title: titleProp,
  subTitle,
  imageUrl,
  url,
  type: typeProp,
  isLast,
  onDismiss,
}) => {
  const t = useCrehanaTranslation();

  const type = (
    url && url.includes('especializacion') ? 'Track' : typeProp || 'Course'
  ) as keyof typeof Icons;
  const title =
    titleProp && type === 'Track'
      ? t('UD_TRACK_TITLE', { interpolation: { title: titleProp } })
      : titleProp;
  const subtitle =
    subTitle && type === 'Project'
      ? t('UD_SEARCH_PROJECT_SUBTITLE', { interpolation: { course: subTitle } })
      : subTitle;

  const history = useHistory();
  const { isDark } = useUITheme();

  const onClick = () => {
    switch (type) {
      case 'Track':
        history.push(
          `${ENROLLMENTS_ROUTE.path}${ENROLLMENTS_TRACKS_ROUTE.path}`,
        );
        break;
      case 'Career':
        history.push(
          `${ENROLLMENTS_ROUTE.path}${ENROLLMENTS_CAREERS_ROUTE.path}`,
        );
        break;
      case 'Bundle':
        history.push(
          `${ENROLLMENTS_ROUTE.path}${ENROLLMENTS_BUNDLES_ROUTE.path}`,
        );
        break;
      default:
        if (url) {
          window.open(url);
        }
    }
    onDismiss();
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    // Open on enter or space
    if (e.keyCode === 13 || e.keyCode === 32) {
      onClick();
    }
  };

  const Icon = Icons[type];
  const titleMaxLines = type === 'Project' ? 1 : 2;

  return (
    <div
      className={clsx(
        'flex items-center py-16 cursor-pointer',
        !isLast
          ? clsx(
              'border-solid border-b',
              isDark ? 'border-base-lighter-16' : 'border-gray-light',
            )
          : '',
      )}
      onClick={onClick}
      onKeyDown={onKeyDown}
    >
      <div
        className={clsx(
          'w-80 h-80 mr-16 flex items-center justify-center flex-shrink-0',
          isDark
            ? 'bg-base-lighter-56 text-gray-dark'
            : 'bg-gray-lighter text-gray-main',
        )}
      >
        {imageUrl ? (
          <LazyImage
            className="w-full h-full"
            imgClassName="object-cover"
            src={imageUrl}
            alt={title || ''}
            dark={isDark}
          />
        ) : (
          <Icon size={40} ignoreColor className="fill-current" />
        )}
      </div>
      <div className="w-full flex flex-col items-start">
        <div className="font-subtitle3">
          <Multiclamp clamp={subtitle ? titleMaxLines : 3}>{title}</Multiclamp>
        </div>
        {subtitle && (
          <div className="font-body3 mt-4">
            <Multiclamp clamp={type === 'Project' ? 2 : 1}>
              {subtitle}
            </Multiclamp>
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchResult;
